import { PrescriptionDetailsKind, PrescriptionDetailsModel, PrescriptionDetailsWriteModel, ServiceKind } from '@lib/guarantees/data-access';

/** Read and write shapes differ only in how the drug is named: products vs the codes behind them. */
export type PrescriptionDetails = PrescriptionDetailsModel | PrescriptionDetailsWriteModel;

/**
 * The part of a service line that decides whether anything outside the record can act on it. Stated
 * structurally so a line being authored answers to it exactly as a stored one does.
 */
export interface ServiceLineShape {
  kind: ServiceKind;
  code?: string;
  isFreeForm?: boolean;
  details?: PrescriptionDetails;
}

/**
 * What the line is missing before anything outside the record can act on it: coverage, limits and
 * the price list all match on the dictionary code, and a pharmacy order is built from the drug's
 * trademarks. Say it here, or it surfaces at ordering time with nothing on screen to explain it.
 *
 * A free-form line is uncoded on purpose and is never unmapped.
 */
export function isPrescriptionUnmapped(prescription: ServiceLineShape): boolean {
  if (prescription.isFreeForm) {
    return false;
  }

  if (!prescription.code?.trim()) {
    return true;
  }

  return prescription.kind === ServiceKind.Medication && !hasMatchedDrugs(prescription.details);
}

function hasMatchedDrugs(details: PrescriptionDetails | undefined): boolean {
  if (!details || details._dt !== PrescriptionDetailsKind.Drugs) {
    return false;
  }

  const matched = 'drugs' in details ? details.drugs : undefined;
  const codes = 'drugCodes' in details ? details.drugCodes : undefined;

  return (matched?.length ?? 0) > 0 || (codes?.length ?? 0) > 0;
}

/** How much of a drug was prescribed, as the two numbers that can state it. */
export interface PrescribedAmount {
  /** Units the course comes to, as the service derives them from the schedule. */
  required?: number;
  /** Packages the course is ordered in, which is what the backend orders against where it is stated. */
  packages?: number;
  /** Both were stated, so the package count is governing and the units are what it replaced. */
  overridden: boolean;
}

/**
 * What a line prescribes, as a number — asked only of a drug. Every other family is one of itself:
 * a course is stored as one line per session, so `quantity` is a 1 on all of them and reporting it
 * would put a figure on a row that says nothing about it.
 *
 * A drug can carry the units its course comes to, the packages it is ordered in, or both — and the
 * two are carried side by side rather than collapsed into whichever wins. How much to buy and how
 * much was written are different answers, and a row showing one under the other's name cannot be
 * checked against the prescription it came from.
 *
 * Read off the details alone: both figures exist on no other family, so a line whose kind says
 * something else while its details say drugs still answers with what it holds.
 */
export function prescribedAmount(prescription: Pick<ServiceLineShape, 'details'>): PrescribedAmount | undefined {
  const details = prescription.details;
  if (details?._dt !== PrescriptionDetailsKind.Drugs) {
    return undefined;
  }

  const required = ('requiredQuantity' in details ? details.requiredQuantity : undefined) || undefined;
  const packages = details.treatmentPackages || undefined;

  return required || packages ? { required, packages, overridden: !!required && !!packages } : undefined;
}
